import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from 'recharts';

// TimeSeriesTile — the canonical Sales small-multiple chart.
//
// Renders: solid line for actual, dashed line for goal (when supplied),
// vertical dashed reference lines for methodology breaks (annotated on
// hover), and gap points where the source data is invalid. The frontend
// never smooths invalid points into the trend — they render as breaks so
// the reader knows the underlying source is broken.
//
// Rate charts clamp the y-axis to 0–100% for month-to-month visual
// comparability. Every tile prints its formula in the footer along with
// the source and clock badge; no chart appears without a freshness label.

const GRID = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const AXIS = { stroke: '#141B4D', strokeOpacity: 0.3 };
const TOOLTIP = {
  contentStyle: { background: '#FFFFFF', border: '1px solid #141B4D', borderRadius: 0, color: '#141B4D', fontSize: 12 },
  labelStyle: { color: '#141B4D', fontWeight: 600 },
};

function fmtValue(v, unit) {
  if (v == null) return '—';
  if (unit === 'rate') return `${(v * 100).toFixed(1)}%`;
  if (unit === 'usd') return v >= 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${Math.round(v)}`;
  return v.toLocaleString();
}

function fmtLatest(actual, goal, unit) {
  const latestActual = [...(actual || [])].reverse().find((p) => p.value != null);
  const latestGoal = [...(goal || [])].reverse().find((p) => p.value != null);
  return { actual: latestActual, goal: latestGoal, unit };
}

function computeMoMDelta(actual, unit) {
  const populated = (actual || []).filter((p) => p.value != null);
  if (populated.length < 2) return null;
  const cur = populated[populated.length - 1].value;
  const prev = populated[populated.length - 2].value;
  if (prev == null || prev === 0) return null;
  if (unit === 'rate') {
    // percentage-point delta for rates
    return { ppDelta: cur - prev, positive: cur >= prev };
  }
  return { pctDelta: (cur - prev) / prev, positive: cur >= prev };
}

const _tsDot = (color) => (props) => {
  const inv = props.payload?.invalid;
  if (inv) {
    return (
      <circle cx={props.cx} cy={props.cy} r={4}
        fill="#FFFFFF" stroke="#C4162A" strokeWidth={2} />
    );
  }
  return <circle cx={props.cx} cy={props.cy} r={2.5} fill={color} />;
};

export function TimeSeriesTile({
  metric,
  methodologyBreaks = [],
  color = '#141B4D',
  goalColor = '#1D57A5',
  height = 140,
  testId,
  compact = false,
}) {
  const { key, label, unit, formula, has_sheet_goal, source, clock, actual, goal } = metric;

  // Merge actual + goal into one dataset for recharts.
  const data = (actual || []).map((p, i) => {
    const g = (goal || [])[i];
    return {
      month: p.month,
      label: p.label,
      short: p.label ? p.label.slice(0, 3) : '',
      actual: p.value,
      invalid: p.invalid,
      goal: g?.value ?? null,
    };
  });

  const latest = fmtLatest(actual, goal, unit);
  const mom = computeMoMDelta(actual, unit);
  const rateAxis = unit === 'rate' ? { domain: [0, 1], tickFormatter: (v) => `${Math.round(v * 100)}%` } : {};

  const relevantBreaks = methodologyBreaks.filter((b) => (b.applies_to || []).includes(key));

  const invalidCount = (actual || []).filter((p) => p.invalid).length;
  const hasAnyData = (actual || []).some((p) => p.value != null);

  return (
    <div
      className="border border-indie-ink/15 bg-white p-4 flex flex-col gap-2 min-h-[220px]"
      data-testid={testId || `timeseries-${key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
            {label}
          </div>
          {!compact && (
            <div className="mt-0.5 text-[10px] font-mono text-indie-ink/45" title={formula}>
              {formula}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xl font-semibold text-indie-ink num-tabular leading-tight">
            {fmtValue(latest.actual?.value, unit)}
          </div>
          {latest.goal?.value != null && (
            <div className="text-[10px] text-indie-ink/60 num-tabular">
              vs goal {fmtValue(latest.goal.value, unit)}
            </div>
          )}
          {mom && (
            <div className={`text-[10px] num-tabular ${mom.positive ? 'text-indie-evergreen' : 'text-indie-neg-dark'}`}>
              {unit === 'rate'
                ? `${mom.positive ? '+' : ''}${(mom.ppDelta * 100).toFixed(1)}pp MoM`
                : `${mom.positive ? '+' : ''}${(mom.pctDelta * 100).toFixed(1)}% MoM`}
            </div>
          )}
        </div>
      </div>

      {hasAnyData ? (
        <div style={{ height }} data-testid={`${testId || `timeseries-${key}`}-chart`}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#141B4D' }} {...AXIS} />
              <YAxis
                tick={{ fontSize: 10, fill: '#141B4D' }}
                width={38}
                {...AXIS}
                {...rateAxis}
                tickFormatter={rateAxis.tickFormatter || ((v) => (unit === 'usd' ? `$${Math.round(v / 1000)}k` : v))}
              />
              <Tooltip
                {...TOOLTIP}
                formatter={(v, name) => [fmtValue(v, unit), name === 'goal' ? 'Goal' : 'Actual']}
              />
              {has_sheet_goal && (
                <Line
                  type="monotone"
                  dataKey="goal"
                  stroke={goalColor}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              <Line
                type="monotone"
                dataKey="actual"
                stroke={color}
                strokeWidth={2}
                dot={_tsDot(color)}
                isAnimationActive={false}
              />
              {relevantBreaks.map((b) => {
                // methodology breaks are dates; we approximate the month via label.
                const brkMonth = parseInt(b.date.split('-')[1], 10);
                const point = data.find((d) => d.month === brkMonth);
                if (!point) return null;
                return (
                  <ReferenceLine
                    key={b.date}
                    x={point.short}
                    stroke="#C4162A"
                    strokeDasharray="4 4"
                    strokeOpacity={0.55}
                    label={{
                      value: b.kind === 'google_ads_attribution' ? 'GA break' : 'Rubric break',
                      position: 'top', fontSize: 9, fill: '#C4162A',
                    }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-xs text-indie-ink/50 italic py-6" data-testid={`${testId || `timeseries-${key}`}-empty`}>
          No history for this metric yet.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
        <span>{source}</span>
        <span className="uppercase tracking-[0.14em]">
          {clock} clock
          {invalidCount > 0 && (
            <span className="ml-2 text-indie-neg-dark font-semibold">
              · {invalidCount} invalid point{invalidCount > 1 ? 's' : ''}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// ClosedSplitTimeSeriesTile — same as above but overlays per-closer lines.
export function ClosedSplitTimeSeriesTile({
  label,
  perCloser,
  seriesKey,
  unit = 'usd',
  formula,
  source = 'Sales Snapshot 2026 · Individual Performance',
  clock = 'cash',
  height = 220,
  testId,
}) {
  // Build merged data: [{month, short, Paul, Chi, Garret}, ...]
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const closers = (perCloser || []).filter((c) => c.present_in_ytd || c.ramping_since);
  const rateAxis = unit === 'rate' ? { domain: [0, 1], tickFormatter: (v) => `${Math.round(v * 100)}%` } : {};

  const data = months.map((m) => {
    const row = { month: m, short: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][m - 1] + (m <= 12 ? '' : '') };
    for (const c of closers) {
      const p = (c.series?.[seriesKey] || []).find((s) => s.month === m);
      row[c.name] = p?.value ?? null;
    }
    return row;
  });

  return (
    <div className="border border-indie-ink/15 bg-white p-4 flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">{label}</div>
          {formula && (
            <div className="mt-0.5 text-[10px] font-mono text-indie-ink/45">{formula}</div>
          )}
        </div>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#141B4D' }} {...AXIS} />
            <YAxis
              tick={{ fontSize: 10, fill: '#141B4D' }}
              width={38}
              {...AXIS}
              {...rateAxis}
              tickFormatter={rateAxis.tickFormatter || ((v) => (unit === 'usd' ? `$${Math.round(v / 1000)}k` : v))}
            />
            <Tooltip
              {...TOOLTIP}
              formatter={(v, name) => [fmtValue(v, unit), name]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {closers.map((c) => (
              <Line
                key={c.name}
                type="monotone"
                dataKey={c.name}
                stroke={c.color}
                strokeWidth={2}
                dot={{ r: 2.5, fill: c.color }}
                connectNulls={false}
                isAnimationActive={false}
                name={c.ramping_since ? `${c.name} (ramping since ${c.ramping_since})` : c.name}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between gap-2 text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
        <span>{source}</span>
        <span className="uppercase tracking-[0.14em]">{clock} clock</span>
      </div>
    </div>
  );
}
